// SPDX-License-Identifier: MIT
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import { inOutCubic, outCubic, outExpo } from '../core/ease.ts';
import { smoothstep, window4 } from '../core/math.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { Camera } from '../three.ts';
import { roundRect } from '../type.ts';
/** A point stretches into a line of light, a measuring grid unfolds and tilts into a floor. */
export default defineScene({
    id: 'signal-grid',
    version: '0.1.0',
    aspects: ['16:9'],
    propsSchema: z.object({ pill: z.optional(z.string().check(z.maxLength(48))) }),
    glyphs: (p) => p.pill ?? '',
    motion: (_p, env) => (env.localBeat < 1.6 ? 0.8 : 0.4),
    draw(ctx, props, env) {
        const { colors } = env.brand;
        const b = env.localBeat;
        const cx = DESIGN_W / 2;
        const cy = DESIGN_H / 2;
        ctx.fillStyle = colors.ink;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        // Floor grid: camera pitches down from beat 2, revealing a perspective plane.
        const tilt = inOutCubic(smoothstep(1.8, 3.6, b));
        const gridIn = outCubic(smoothstep(1.0, 2.6, b));
        if (gridIn > 0) {
            const cam = new Camera({ fov: 60, position: [0, -260 * tilt, 0], pitch: 0.42 * tilt });
            ctx.lineWidth = 1.5;
            const span = 2400 * gridIn;
            for (let i = -16; i <= 16; i++) {
                const x = (i / 16) * span;
                const a = cam.project([x, 320, 120]);
                const c = cam.project([x, 320, 5200]);
                if (!a || !c)
                    continue;
                ctx.strokeStyle = rgba(colors.primary, 0.12 + 0.28 * gridIn * (1 - Math.abs(i) / 18));
                ctx.beginPath();
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(c.x, c.y);
                ctx.stroke();
            }
            const scroll = (env.local * 900) % 260;
            for (let k = 0; k < 20; k++) {
                const z = 120 + k * 260 - scroll;
                const l = cam.project([-span, 320, z]);
                const r = cam.project([span, 320, z]);
                if (!l || !r)
                    continue;
                ctx.strokeStyle = rgba(colors.primary, 0.35 * gridIn * (1 - k / 20));
                ctx.beginPath();
                ctx.moveTo(l.x, l.y);
                ctx.lineTo(r.x, r.y);
                ctx.stroke();
            }
            // Horizon glow charging towards the next scene.
            const charge = smoothstep(2.2, 4.0, b);
            const horizon = cam.project([0, 320, 5200]);
            if (horizon && charge > 0) {
                const g = ctx.createRadialGradient(cx, horizon.y, 0, cx, horizon.y, 900);
                g.addColorStop(0, rgba(colors.accent, 0.85 * charge));
                g.addColorStop(0.25, rgba(colors.primary, 0.35 * charge));
                g.addColorStop(1, rgba(colors.primary, 0));
                ctx.fillStyle = g;
                ctx.fillRect(0, horizon.y - 600, DESIGN_W, 1200);
            }
        }
        // The signal: dot → line with ticks.
        const dot = smoothstep(0, 0.35, b) * (1 - smoothstep(2.2, 3.0, b));
        const len = 1640 * outExpo(smoothstep(0.35, 1.4, b));
        if (dot > 0) {
            const lineY = cy - 220 * tilt;
            const glow = ctx.createRadialGradient(cx, lineY, 0, cx, lineY, 140);
            glow.addColorStop(0, rgba('#FFFFFF', dot));
            glow.addColorStop(0.2, rgba(colors.accent, 0.6 * dot));
            glow.addColorStop(1, rgba(colors.accent, 0));
            ctx.fillStyle = glow;
            ctx.fillRect(cx - 160, lineY - 160, 320, 320);
            ctx.fillStyle = rgba('#FFFFFF', dot);
            ctx.fillRect(cx - len / 2, lineY - 2, len, 4);
            const ticks = Math.floor(len / 40);
            for (let i = 0; i <= ticks; i++) {
                const x = cx - len / 2 + i * 40;
                const major = i % 5 === 0;
                ctx.fillStyle = rgba(colors.paper, dot * (major ? 0.8 : 0.35));
                ctx.fillRect(x - 1, lineY + 10, 2, major ? 22 : 10);
            }
        }
        if (props.pill) {
            const a = window4(0.8, 1.5, 3.3, 3.9, b);
            if (a > 0) {
                ctx.font = font(env.brand, 500, 26, 'mono');
                ctx.letterSpacing = '6px';
                const w = ctx.measureText(props.pill).width + 64;
                const y = 190 + 20 * (1 - a);
                ctx.globalAlpha = a;
                roundRect(ctx, cx - w / 2, y, w, 58, 29);
                ctx.strokeStyle = rgba(colors.accent, 0.8);
                ctx.lineWidth = 2;
                ctx.stroke();
                ctx.fillStyle = colors.paper;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(props.pill, cx, y + 30);
                env.report?.text('pill', [cx - w / 2, y, w, 58], a);
                ctx.globalAlpha = 1;
                ctx.letterSpacing = '0px';
            }
        }
    },
});
